import axios from './axiositeachWrapper';
import Config from '../_config/config';

/**
 * Clinical Masters — the first-class clinical layer behind the Decision
 * Resource Studio: therapeutic-area domains, conditions (M2M with a primary
 * domain), the clinical-source registry with study-resource documents,
 * the parameter vocabulary (LOINC-coded), medication classes (ATC-keyed),
 * and shared governed config (§6 rule classes, artifacts, roles).
 *
 * Backed by real tables (nodefork /v1/clinical-masters); replaces the
 * retired domain-master resource (#2933).
 */

export interface ConditionDomainLink {
  domainKey: string;
  isPrimary: boolean;
}

export interface MasterDomain {
  id?: number;
  domainKey: string;
  name: string;
  description?: string | null;
}

export interface MasterCondition {
  id?: number;
  conditionKey: string;
  name: string;
  status: 'active' | 'planned';
  description?: string | null;
  domains: ConditionDomainLink[];
  sourceKeys: string[];
}

export interface MasterSource {
  id?: number;
  sourceKey: string;
  name: string;
  publisher?: string | null;
  year?: string | null;
  type: 'guideline' | 'drug-labeling' | 'specialty-guidance' | 'client-policy' | string;
  approvalStatus: 'approved' | 'draft' | 'retired' | string;
  ragStatusManual?: string | null;
  /** Derived server-side: from linked documents' RAG pipeline state, or the manual value for document-less sources. */
  ragStatus?: 'ragified' | 'pending' | 'not-applicable' | string;
  resourceIds: number[];
}

export interface MasterParameter {
  id?: number;
  paramKey: string;
  name: string;
  /** Clinical short form (SBP, K+, eGFR…) — display/search alias. */
  abbreviation?: string | null;
  code?: string | null;
  codeSystem?: string | null;
  dataType: 'numeric' | 'boolean' | 'coded' | 'text' | string;
  units?: string | null;
  category?: string | null;
}

export interface MasterMedicationClass {
  id?: number;
  classKey: string;
  name: string;
  atcCode?: string | null;
  description?: string | null;
}

export interface SharedMasters {
  ruleClasses?: Array<{ id: string; name: string; definition: string; specRef?: string }>;
  specificationArtifacts?: Array<{ id: string; name: string; specRef?: string }>;
  roles?: string[];
}

export interface ClinicalMasters {
  domains: MasterDomain[];
  conditions: MasterCondition[];
  sources: MasterSource[];
  parameters: MasterParameter[];
  medicationClasses: MasterMedicationClass[];
  shared: SharedMasters | null;
}

class ClinicalMastersService {
  async getClinicalMasters(): Promise<ClinicalMasters> {
    const url = `${Config.nodeSiteUrl}v1/clinical-masters`;
    const response = await axios.get<{ success: boolean; data: ClinicalMasters; message: string }>(url);
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Failed to load clinical masters');
    }
    return response.data.data;
  }

  /**
   * Bulk sync. Entity arrays present in the payload are synced fully
   * (upsert by key, delete-missing); absent arrays are untouched.
   * Throws with the server message on rejection (e.g. 409 deleting a
   * source still referenced by a condition).
   */
  async saveClinicalMasters(masters: Partial<ClinicalMasters>): Promise<void> {
    const url = `${Config.nodeSiteUrl}v1/clinical-masters`;
    const response = await axios.put(url, masters);
    const body = response.data as unknown as { success: boolean; message?: string };
    if (!body?.success) {
      throw new Error(body?.message || 'Failed to save clinical masters');
    }
  }
}

export default new ClinicalMastersService();
